import React from "react";
import { Link } from "react-router-dom";
import { Heart, Plus, Minus, Box, Check } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { displayName, priceLabel, hasPrice } from "@/lib/api";
import { toast } from "sonner";

const StockDot = ({ inStock, text }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium">
    <span className={`w-2 h-2 rounded-full ${inStock ? "bg-emerald-500" : "bg-steel-light"}`} />
    <span className={inStock ? "text-emerald-600" : "text-steel-light"}>{text || (inStock ? "In stock" : "Out of stock")}</span>
  </span>
);

export const ProductCard = ({ product, compact = false }) => {
  const { addToCart, favourites, toggleFav } = useStore();
  const [qty, setQty] = React.useState(1);
  const fav = favourites.includes(product.sku);

  const add = (e) => {
    e.preventDefault();
    addToCart(product.sku, qty);
    toast.success(`Added ${qty} × ${product.sku}`, { description: displayName(product) });
  };

  return (
    <div data-testid={`product-card-${product.sku}`} className="group relative flex flex-col bg-white border border-border rounded-md overflow-hidden hover:border-graphite/40 hover:shadow-[0_2px_12px_rgba(17,24,39,0.06)] transition-all">
      <button
        data-testid={`fav-btn-${product.sku}`}
        onClick={(e) => { e.preventDefault(); toggleFav(product.sku); }}
        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-md grid place-items-center border transition-colors ${fav ? "bg-orange/10 border-orange text-orange" : "bg-white/80 border-border text-steel-light hover:text-graphite"}`}
      >
        <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} />
      </button>

      <Link to={`/product/${encodeURIComponent(product.sku)}`} className={`gp-card-img grid place-items-center ${compact ? "aspect-square" : "aspect-[4/3]"} p-4`}>
        {product.main_image
          ? <img src={product.main_image} alt={displayName(product)} loading="lazy" className="max-h-full max-w-full object-contain mix-blend-multiply group-hover:scale-[1.03] transition-transform" />
          : <Box className="w-10 h-10 text-steel-light/50" />}
      </Link>

      <div className="flex flex-col flex-1 p-3 gap-1.5">
        <div className="mono text-[11px] text-orange font-medium truncate">{product.sku}</div>
        <Link to={`/product/${encodeURIComponent(product.sku)}`} className="text-sm font-medium text-graphite leading-snug line-clamp-2 hover:text-orange transition-colors min-h-[2.5rem]">
          {displayName(product)}
        </Link>
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-xs text-steel-light truncate max-w-[55%]">{product.brand || product.category || "—"}</span>
          <StockDot inStock={product.is_in_stock} text={product.stock_status} />
        </div>
        <div className={`text-sm font-bold ${hasPrice(product) ? "text-graphite" : "text-steel-light"}`}>{priceLabel(product)}</div>

        <div className="mt-auto pt-2 flex items-center gap-2">
          <div className="flex items-center border border-border rounded-md h-9">
            <button data-testid={`qty-minus-${product.sku}`} onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-full grid place-items-center text-steel hover:text-graphite"><Minus className="w-3.5 h-3.5" /></button>
            <span className="mono w-8 text-center text-sm">{qty}</span>
            <button data-testid={`qty-plus-${product.sku}`} onClick={() => setQty((q) => q + 1)} className="w-8 h-full grid place-items-center text-steel hover:text-graphite"><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <button data-testid={`quick-add-${product.sku}`} onClick={add} className="flex-1 h-9 inline-flex items-center justify-center gap-1.5 bg-graphite hover:bg-orange text-white text-sm font-medium rounded-md transition-colors">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>
    </div>
  );
};
